import type { PageKey } from '~~/types/rbac'
import { until } from '@vueuse/core'

/**
 * Global RBAC route-gating middleware (runs on every navigation).
 *
 * This is the UI layer of the defense-in-depth model described in the design
 * doc: it gates navigation purely as a usability aid so members are not led to
 * pages they cannot use. It is intentionally NOT the authoritative check —
 * Nitro route guards and Postgres RLS remain the trusted enforcement layers, so
 * this middleware fails open whenever permissions cannot be resolved.
 *
 * Behaviour (Req 3.4, 5.5, 11.4):
 * - Routes that do not map to a gated page (login `/`, the OAuth callback
 *   `/account`, `/access-denied`, etc.) are always allowed through.
 * - Unauthenticated visitors are left to the Supabase auth flow (it handles the
 *   login redirect); this middleware does not block them.
 * - When the authenticated member lacks the page permission for the target
 *   route, they are redirected to the first page they *can* see, or to
 *   `/access-denied` when they can see none.
 */

interface GatedRoute {
  /** The page-permission key that governs this route. */
  page: PageKey
  /** Path prefix used to match the target route to this gated page. */
  match: string
  /** Concrete path to redirect to when sending a member to this page. */
  redirect: string
}

/**
 * Maps route paths to the {@link PageKey} that gates them, based on the pages
 * under `app/pages/`. Order defines the priority used when picking the first
 * page an under-privileged member is allowed to see. The `import` page lives at
 * `/import/tiktok`, so its redirect target differs from its match prefix.
 */
const GATED_ROUTES: GatedRoute[] = [
  { page: 'dashboard', match: '/dashboard', redirect: '/dashboard' },
  { page: 'schedules', match: '/schedules', redirect: '/schedules' },
  { page: 'import', match: '/import', redirect: '/import/tiktok' },
  { page: 'notifications', match: '/notifications', redirect: '/notifications' },
  { page: 'massdistribution', match: '/massdistribution', redirect: '/massdistribution' },
  { page: 'comments', match: '/comments', redirect: '/comments' },
]

/** Returns the gated route for a path, or `null` when the path is ungated. */
function resolveGatedRoute(path: string): GatedRoute | null {
  return GATED_ROUTES.find(
    route => path === route.match || path.startsWith(`${route.match}/`),
  ) ?? null
}

export default defineNuxtRouteMiddleware(async (to) => {
  // Ungated routes (login, account/callback, access-denied, …) pass through.
  const gated = resolveGatedRoute(to.path)
  if (!gated) return

  // Leave unauthenticated visitors to the Supabase login redirect flow.
  const user = useSupabaseUser()
  if (!user.value) return

  // Ensure the member's permissions are resolved before deciding. The composable
  // may already be loading (its watcher fires on auth), so kick a load and wait
  // until it has settled at least once rather than racing the in-flight fetch.
  // The wait is kept short: permissions are cached after the first resolution
  // (awaited during SSR by the permissions plugin), and when they cannot be
  // resolved quickly we fail open instead of freezing the navigation — the
  // server guards and RLS remain the authoritative enforcement layers.
  const permissions = usePermissions()
  void permissions.load()
  if (!permissions.loaded.value) {
    await until(() => permissions.loaded.value).toBe(true, {
      timeout: 1500,
      throwOnTimeout: false,
    })
  }

  // Fail open if permissions could not be resolved (e.g. transient/SSR failure):
  // the server guards and RLS are the authoritative layers.
  if (!permissions.loaded.value || permissions.error.value) return

  // Authorized — admins implicitly hold the full page catalog.
  if (permissions.canSeePage(gated.page)) return

  // Not allowed for this page: show the access-denied view (the sidebar still
  // lists every page, so this is how disallowed access is surfaced).
  return navigateTo('/access-denied')
})
